import type { BottomSheetModal } from "@gorhom/bottom-sheet";
import type {
	Choice,
	ItemFeedback,
	ItemResponse,
	OriginalItemTypes,
	ToeicArtifactType,
	UnifiedCitation,
} from "@memorang/types";
import type { ReactNode } from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import * as Animatable from "react-native-animatable";
import { Button } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { hasStaticFeedback } from "@memorang/helpers/feedback";
import { isWeb } from "@memorang/helpers/platform";
import { isAiSubmittable } from "@memorang/helpers/session";
import type { FontSchemaType } from "../../font";
import { useDevice } from "../../useDevice";
import BottomSheetModalWrapper, {
	type CustomHandle,
} from "../BottomSheetModalWrapper";
import Box from "../Box";
import type { Stat } from "../RowStats";
import type { SubSkillItem } from "./AiPostAnswerHeader";
import { PostAnswerDetailBlock } from "./PostAnswerDetailBlock";
import type { PostAnswerDetailBlockProps } from "./PostAnswerDetailBlock";
import { PostAnswerFollowUpsBlock } from "./PostAnswerFollowUpsBlock";
import type { PostAnswerFollowUps } from "./PostAnswerFollowUpsBlock";
import PostAnswerSheetHandle from "./PostAnswerSheetHandle";
import { PostAnswerStatsBlock } from "./PostAnswerStatsBlock";
import {
	getStudySessionBottomBarHeight,
	postAnswerSnapIndex,
	postAnswerSnapPoint,
	sessionMaxWidth,
} from "./constants";

type PostAnswerPanelAssistant = {
	icon: string;
	alias: string;
	onPress?: () => void;
};

type PostAnswerPanelItem = {
	id: number;
	responseType: ItemResponse["type"];
	itemType?: OriginalItemTypes;
	toeicArtifactType?: ToeicArtifactType;
	choices: Choice[];
	feedback?: ItemFeedback;
	citations?: UnifiedCitation[];
	showCitations?: boolean;
	tags?: string[];
};

type PostAnswerPanelExplanation = {
	title: string;
	appFont: FontSchemaType;
	fontSize: number;
	language: string;
	currentAiFeedback?: string;
	persistedAiFeedback?: string;
	emptyFeedbackText?: string;
	renderFeedbackMenu?: () => ReactNode;
	renderTtsContainer?: (explanation?: string) => ReactNode;
	streaming?: boolean;
	visible?: boolean;
	forceDynamic?: boolean;
};

type PostAnswerPanelLayout = {
	isSplitLayout?: boolean;
	isSummativeTest?: boolean;
};

type PostAnswerPanelScore = {
	stats: Stat[];
	maximumScore: number;
	overallScoreLabel: string;
	score?: number;
	subskillAssessments: SubSkillItem[];
	showAiHeader?: boolean;
};

type PostAnswerPanelState = {
	itemAnswered?: boolean;
	itemIndex: number;
	questionAnswered?: boolean;
	streaming?: boolean;
};

export type PostAnswerPanelValue = {
	aiAssistant: PostAnswerPanelAssistant;
	answerState: PostAnswerPanelState;
	explanation: PostAnswerPanelExplanation;
	followUps?: PostAnswerFollowUps;
	handleExpandCollapse: () => void;
	handleNavigateToReference: (reference: UnifiedCitation) => void;
	showTextBasedCitationOnly?: boolean;
	handleTrackSheetOpened?: () => void;
	headerIcon?: ReactNode;
	headerLabel: string;
	item: PostAnswerPanelItem;
	layout?: PostAnswerPanelLayout;
	score: PostAnswerPanelScore;
	suggestCorrection?: {
		cta: string;
		handleClick: () => void;
	};
};

type PostAnswerPanelProps = {
	children: ReactNode;
	value: PostAnswerPanelValue;
};

const PostAnswerPanelContext = createContext<PostAnswerPanelValue | null>(null);

const usePostAnswerPanel = () => {
	const value = useContext(PostAnswerPanelContext);

	if (!value) {
		throw new Error(
			"PostAnswerPanel compound components must be rendered inside PostAnswerPanel.",
		);
	}

	return value;
};

const getPostAnswerDetailBlockProps = (
	value: PostAnswerPanelValue,
): PostAnswerDetailBlockProps => ({
	aiAssistant: value.aiAssistant,
	content: {
		explanationTitle: value.explanation.title,
		feedback: value.item.feedback,
		choices: value.item.choices,
		tags: value.item.tags,
		citations: value.item.citations,
		currentAiFeedback: value.explanation.currentAiFeedback,
		persistedAiFeedback: value.explanation.persistedAiFeedback,
		emptyFeedbackText: value.explanation.emptyFeedbackText,
	},
	scoring: {
		score: value.score.score,
		maximumScore: value.score.maximumScore,
		overallScoreLabel: value.score.overallScoreLabel,
		subskillAssessments: value.score.subskillAssessments,
	},
	rendering: {
		appFont: value.explanation.appFont,
		fontSize: value.explanation.fontSize,
		language: value.explanation.language,
		renderFeedbackMenu: value.explanation.renderFeedbackMenu,
		renderTtsContainer: value.explanation.renderTtsContainer,
		streaming: value.explanation.streaming,
	},
	responseType: value.item.responseType,
	itemType: value.item.itemType,
	toeicArtifactType: value.item.toeicArtifactType,
	showCitations: value.item.showCitations,
	showAiHeader: value.score.showAiHeader,
	showExplanation: value.explanation.visible,
	forceDynamicExplanation: value.explanation.forceDynamic,
	handleExpandCollapse: value.handleExpandCollapse,
	handleNavigateToReference: value.handleNavigateToReference,
	showTextBasedCitationOnly: value.showTextBasedCitationOnly,
	headerIcon: value.headerIcon,
	headerLabel: value.headerLabel,
});

const PostAnswerPanelProvider = ({ children, value }: PostAnswerPanelProps) => (
	<PostAnswerPanelContext.Provider value={value}>
		{children}
	</PostAnswerPanelContext.Provider>
);

const PostAnswerPanelStats = () => {
	const { score } = usePostAnswerPanel();
	return <PostAnswerStatsBlock stats={score.stats} />;
};

const resolveIsAiItemType = (
	item: PostAnswerPanelItem,
	forceDynamic?: boolean,
): boolean =>
	isAiSubmittable(
		item.responseType,
		item.itemType,
		item.toeicArtifactType,
		hasStaticFeedback(item.feedback),
	) || Boolean(forceDynamic);

const PostAnswerPanelDetail = () => {
	const value = usePostAnswerPanel();
	const { isDesktopWeb } = useDevice();
	const isAiItemType = resolveIsAiItemType(
		value.item,
		value.explanation.forceDynamic,
	);
	const showAiModalHandle = isDesktopWeb && isAiItemType;

	return (
		<PostAnswerDetailBlock
			{...getPostAnswerDetailBlockProps(value)}
			showAiModalHandle={showAiModalHandle}
		/>
	);
};

const PostAnswerPanelFollowUps = () => {
	const { followUps, item } = usePostAnswerPanel();
	return <PostAnswerFollowUpsBlock followUps={followUps} itemId={item.id} />;
};

const PostAnswerPanelBody = () => {
	const { answerState } = usePostAnswerPanel();
	const { isMobile } = useDevice();

	return (
		<Box gap={isMobile ? 0 : 16}>
			{answerState.questionAnswered ? (
				<>
					<PostAnswerPanelStats />
					<PostAnswerPanelDetail />
				</>
			) : null}
			<PostAnswerPanelFollowUps />
		</Box>
	);
};

const getMaxWidth = (isSummativeTest?: boolean) =>
	isSummativeTest ? sessionMaxWidth.summative : sessionMaxWidth.formative;

const getAlignSelf = (isSplitLayout?: boolean, isSummativeTest?: boolean) => {
	if (isSplitLayout) {
		return undefined;
	}
	return isSummativeTest ? "flex-start" : "center";
};

const PostAnswerPanelFrame = ({ children }: { children: ReactNode }) => {
	const { layout } = usePostAnswerPanel();

	return (
		<Box
			maxWidth={getMaxWidth(layout?.isSummativeTest)}
			alignSelf={getAlignSelf(layout?.isSplitLayout, layout?.isSummativeTest)}
			width="100%"
		>
			{children}
		</Box>
	);
};

const ModalPostAnswerSheet = ({ children }: { children: ReactNode }) => {
	const panelValue = usePostAnswerPanel();
	const {
		aiAssistant,
		answerState,
		handleTrackSheetOpened,
		headerIcon,
		headerLabel,
		item,
		layout,
	} = panelValue;
	const { isMobile, isMobileWeb } = useDevice();
	const insets = useSafeAreaInsets();
	const [snapIndex, setSnapIndex] = useState<number>(postAnswerSnapIndex.mid);
	const [presentIndex, setPresentIndex] = useState<number>(
		postAnswerSnapIndex.mid,
	);
	const rememberedIndexRef = useRef<number>(postAnswerSnapIndex.mid);
	const [expandedSnapEnabled, setExpandedSnapEnabled] = useState(false);
	const bottomSheetRef = useRef<BottomSheetModal>(null);
	const isAiItemType = resolveIsAiItemType(
		item,
		panelValue.explanation.forceDynamic,
	);
	const bottomBarInset = getStudySessionBottomBarHeight({
		safeAreaBottom: insets.bottom,
		isMobile,
		isSummativeTest: layout?.isSummativeTest,
	});
	const snapPoints = useMemo(() => {
		const midSnapPoint = isAiItemType
			? postAnswerSnapPoint.ai
			: postAnswerSnapPoint.default;

		if (isMobileWeb && isAiItemType && !expandedSnapEnabled) {
			return [postAnswerSnapPoint.peek, midSnapPoint, midSnapPoint];
		}

		return [postAnswerSnapPoint.peek, midSnapPoint, "90%"];
	}, [expandedSnapEnabled, isAiItemType, isMobileWeb]);

	useEffect(() => {
		if (snapIndex === postAnswerSnapIndex.expanded) {
			handleTrackSheetOpened?.();
		}
	}, [snapIndex, handleTrackSheetOpened]);

	const handleSetSnapIndex = useCallback((index: number) => {
		setSnapIndex(index);
		if (index >= 0 && index < postAnswerSnapIndex.expanded) {
			rememberedIndexRef.current = index;
		}
	}, []);

	useEffect(() => {
		if (!answerState.itemAnswered) {
			setPresentIndex(rememberedIndexRef.current);
			setSnapIndex(rememberedIndexRef.current);
		}
	}, [answerState.itemAnswered]);

	const handleAiInteractionStart = useCallback(() => {
		if (isMobileWeb && snapIndex !== postAnswerSnapIndex.expanded) {
			setExpandedSnapEnabled(true);
		}
	}, [isMobileWeb, snapIndex]);

	const handleToggleCollapse = useCallback(() => {
		bottomSheetRef.current?.snapToIndex(
			snapIndex === postAnswerSnapIndex.peek
				? postAnswerSnapIndex.mid
				: postAnswerSnapIndex.peek,
		);
	}, [snapIndex]);

	const CustomHandle = useCallback<CustomHandle>(
		() => (
			<PostAnswerSheetHandle
				handleExpandCollapse={handleToggleCollapse}
				handleInteractionStart={
					isMobileWeb && isAiItemType ? handleAiInteractionStart : undefined
				}
				aiAssistant={isAiItemType ? aiAssistant : undefined}
				headerIcon={isAiItemType ? headerIcon : undefined}
				headerLabel={isAiItemType ? headerLabel : undefined}
			/>
		),
		[
			aiAssistant,
			handleAiInteractionStart,
			handleToggleCollapse,
			headerIcon,
			headerLabel,
			isAiItemType,
			isMobileWeb,
		],
	);

	return (
		<PostAnswerPanelFrame>
			<BottomSheetModalWrapper
				ref={bottomSheetRef}
				showModal={answerState.itemAnswered}
				snapPoints={snapPoints}
				handleSetSnapIndex={handleSetSnapIndex}
				snapIndex={presentIndex}
				animateOnMount
				enableDismissOnClose={isWeb}
				customHandle={CustomHandle}
				backdropPressBehavior={postAnswerSnapIndex.mid}
				backdropAppearsOnIndex={postAnswerSnapIndex.expanded}
				backdropDisappearsOnIndex={postAnswerSnapIndex.mid}
				bottomInset={bottomBarInset}
				contentContainerStyle={{ paddingHorizontal: 0 }}
			>
				{/* Re-provide context: bottom sheet renders in a portal outside the React tree */}
				<PostAnswerPanelContext.Provider value={panelValue}>
					{children}
				</PostAnswerPanelContext.Provider>
			</BottomSheetModalWrapper>
		</PostAnswerPanelFrame>
	);
};

const PostAnswerPanelSurface = ({ children }: { children: ReactNode }) => {
	const { isDesktopWeb } = useDevice();

	if (isDesktopWeb) {
		return (
			<Animatable.View
				useNativeDriver
				duration={300}
				animation="fadeIn"
				easing="ease-out"
			>
				<PostAnswerPanelFrame>{children}</PostAnswerPanelFrame>
			</Animatable.View>
		);
	}

	return <ModalPostAnswerSheet>{children}</ModalPostAnswerSheet>;
};

const PostAnswerPanelSuggestCorrection = () => {
	const { answerState, suggestCorrection } = usePostAnswerPanel();

	if (answerState.streaming || !suggestCorrection) {
		return null;
	}

	return (
		<Box
			alignSelf="center"
			marginTop={16}
			paddingBottom={100}
			paddingHorizontal={16}
		>
			<Button icon="flag-outline" onPress={suggestCorrection.handleClick}>
				{suggestCorrection.cta}
			</Button>
		</Box>
	);
};

export const PostAnswerPanel = Object.assign(PostAnswerPanelProvider, {
	Body: PostAnswerPanelBody,
	Detail: PostAnswerPanelDetail,
	FollowUps: PostAnswerPanelFollowUps,
	Frame: PostAnswerPanelFrame,
	Stats: PostAnswerPanelStats,
	SuggestCorrection: PostAnswerPanelSuggestCorrection,
	Surface: PostAnswerPanelSurface,
});
